import React, { useEffect } from 'react';
import T from 'i18n';
import { Chip, IconButton, Stack, TextField, Typography } from '@mui/material';
import {
  FilterListOutlined,
  SendOutlined,
  TuneOutlined,
  MarginOutlined,
} from '@mui/icons-material';
import { useFetchMatches } from '../redux/fetchMatches';
import { useGenerateAnswers } from '../redux/generateAnswers';
import { useUpdateUserSettings } from 'features/common/redux/updateUserSettings';
import SearchOptions from './SearchOptions';
import RecentSearchesList from './RecentSearchesList';
import TrialBundlesMenu from './TrialBundlesMenu';
import OutputFormatMenu from './OutputFormatMenu';
import FilesSelectionDialog from './FilesSelectionDialog';
import { useSetAISearchState } from '../redux/setAISearchState';
import useAIHandler from './hooks/useAIHandler';

interface SearchInputProps {
  handleSearch: (input: string, filters: any, outputType?: string) => void;
  isNewSearch?: boolean;
  containsMatches?: boolean;
  matches?: Array<any>;
  isAnswersMode?: boolean;
  fileSidebarAskAI?: boolean;
  gridView?: boolean;
  file?: any;
  inputFilters?: any; // Added filters prop to pass down current filters
}

interface FileItem {
  id: string;
  name: string;
  pageCount?: string;
  size?: number;
  type?: string;
  originalName?: string;
}

const SearchFiltersChip = ({ filters, isAnswersMode, file, type }: any) => {
  if (type === 'location' && filters.location.length === 0) {
    return (
      <Typography sx={{ display: 'inline', color: 'GrayText' }}>
        <b>{T.translate('case.folders')}</b>
      </Typography>
    );
  } else if (type === 'files' && filters.files.length === 0) {
    return (
      <Typography sx={{ display: 'inline', color: 'GrayText' }}>
        <b>{T.translate('case.files')}</b>
      </Typography>
    );
  }
  const truncateText = (text: string, maxLength: number = file ? 20 : 45) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <>
      {isAnswersMode && type === 'location' && filters.location.length > 0 ? (
        <Typography sx={{ display: 'inline', color: 'GrayText' }}>
          <b>{T.translate('case.matches')}</b> : {truncateText(filters.location[0].name)}
          {filters.location.length > 1 ? ` & ${filters.location.length - 1} more ...` : ''}{' '}
        </Typography>
      ) : !isAnswersMode && type === 'location' && filters.location.length > 0 ? (
        <Typography sx={{ display: 'inline', color: 'GrayText' }}>
          <b>{T.translate('case.folders')}</b> : {truncateText(filters.location[0].name)}
          {filters.location.length > 1 ? ` & ${filters.location.length - 1} more ...` : ''}{' '}
        </Typography>
      ) : !isAnswersMode && type === 'files' && filters.files.length > 0 ? (
        <Typography sx={{ display: 'inline', color: 'GrayText' }}>
          <b>{T.translate('case.files')}</b> : {truncateText(filters.files[0].id)}
          {filters.files.length > 1 ? ` & ${filters.files.length - 1} more ...` : ''}
        </Typography>
      ) : null}
    </>
  );
};

export default function SearchInput({
  handleSearch,
  isNewSearch,
  isAnswersMode,
  fileSidebarAskAI,
  gridView,
  file,
  inputFilters,
}: SearchInputProps) {
  const inpRef = React.useRef<HTMLInputElement>(null);
  const { filters, setFilters, handleChangeFilters } = useAIHandler({
    initialFilters: inputFilters,
  });
  // Sync filters with inputFilters when inputFilters changes
  useEffect(() => {
    if (inputFilters) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...inputFilters,
      }));
    }
  }, [inputFilters, setFilters]);

  const { fetchDocumentsPending } = useFetchMatches();
  const { generateAnswersPending } = useGenerateAnswers();
  const { userSettings, updateUserSettings } = useUpdateUserSettings();
  const { searchAnsChat } = useSetAISearchState();
  const [shouldRestoreFocus, setShouldRestoreFocus] = React.useState(false);
  const [searchOptionsAnchorEl, setSearchOptionsAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const openSearchOptions = Boolean(searchOptionsAnchorEl);
  const [recentSearchesAnchorEl, setRecentSearchesAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const openRecentSearches = Boolean(recentSearchesAnchorEl);
  const [filtersMenu, setFiltersMenu] = React.useState<null | HTMLElement>(null);
  const openFiltersMenu = Boolean(filtersMenu);
  const [outputFormatAnchorEl, setOutputFormatAnchorEl] = React.useState<null | HTMLElement>(null);
  const openOutputFormatMenu = Boolean(outputFormatAnchorEl);
  const [filesPopupOpen, setFilesPopupOpen] = React.useState(false);

  // Get output format from userSettings, fallback to 'list' if not set
  const getOutputFormatFromSettings = React.useCallback(() => {
    const savedFormat = userSettings?.aiSettings?.outputFormat;
    if (savedFormat === 'table') return 'table';
    if (savedFormat === 'list') return 'list';
    if (savedFormat === 'freeform' || savedFormat === 'automatic' || savedFormat === 'free form')
      return 'freeform';
    if (savedFormat === 'memo') return 'memo';
    if (savedFormat === 'briefing') return 'briefing';
    if (savedFormat === 'chronology') return 'chronology';
    return 'list'; // Default fallback
  }, [userSettings?.aiSettings?.outputFormat]);

  // Get display label for output format
  const getOutputFormatLabel = (format: string) => {
    const formatLabels: { [key: string]: string } = {
      table: 'Table',
      list: 'List',
      freeform: 'Free Form',
      memo: 'Memo',
      briefing: 'Briefing',
      chronology: 'Chronology',
    };
    return formatLabels[format] || 'List';
  };

  const [selectedOutputFormat, setSelectedOutputFormat] = React.useState<string>(
    getOutputFormatFromSettings(),
  );

  // Update selectedOutputFormat when userSettings changes
  React.useEffect(() => {
    setSelectedOutputFormat(getOutputFormatFromSettings());
  }, [userSettings?.aiSettings?.outputFormat, getOutputFormatFromSettings]);
  const isSummaryMode =
    !isNewSearch &&
    searchAnsChat?.length > 0 &&
    searchAnsChat?.find((ans: any) => ans.command === 'summarize');

  // Effect to restore focus after typing effects complete
  React.useEffect(() => {
    if (shouldRestoreFocus && !fetchDocumentsPending && !generateAnswersPending) {
      const timer = setTimeout(() => {
        if (inpRef.current && !document.activeElement?.closest('#ai-search-input')) {
          inpRef.current.focus();
        }
        setShouldRestoreFocus(false);
      }, 300); // Small delay to ensure typing effect is complete

      return () => clearTimeout(timer);
    }
  }, [shouldRestoreFocus, fetchDocumentsPending, generateAnswersPending]);

  // Set flag to restore focus when search starts
  React.useEffect(() => {
    if (fetchDocumentsPending || generateAnswersPending) {
      setShouldRestoreFocus(true);
    }
  }, [fetchDocumentsPending, generateAnswersPending]);

  const handleRecentSearchesMenuClose = () => {
    setRecentSearchesAnchorEl(null);
  };

  const handleOutputFormatSelect = (format: string) => {
    setSelectedOutputFormat(format);

    const updatedAiSettings = {
      ...userSettings?.aiSettings,
      outputFormat: format, // Use the format directly as it's already lowercase
    };

    updateUserSettings({
      userSettings: {
        ...userSettings,
        aiSettings: updatedAiSettings,
      },
    });
  };

  const startSearch = (input: string) => {
    // Process the input based on selected output format
    const finalInput = input.trim();

    // Use selectedOutputFormat with same fallback logic as getOutputFormatFromSettings
    const outputType = selectedOutputFormat || getOutputFormatFromSettings();
    handleSearch(finalInput, filters, outputType);
    setFilters({ ...filters, phrase: '' });
  };

  const handleSaveFiles = (files: FileItem[]) => {
    // Update the filters with selected files
    handleChangeFilters({ files });
    setFilesPopupOpen(false);
  };

  const handleCloseFilesPopup = () => {
    setFilesPopupOpen(false);
  };
  return (
    <div>
      <style>
        {`
          .locations-scrollbar::-webkit-scrollbar,
          .files-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          
          .locations-scrollbar::-webkit-scrollbar-track,
          .files-scrollbar::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          
          .locations-scrollbar::-webkit-scrollbar-thumb,
          .files-scrollbar::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 10px;
          }
          
          .locations-scrollbar::-webkit-scrollbar-thumb:hover,
          .files-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        `}
      </style>
      <div
        style={{
          border: '1px  solid #0000003B',
          borderRadius: '0.5rem',
          height: 'auto',
          padding: gridView ? '0.1 rem' : '0.5rem',
          marginLeft: '10px',
          backgroundColor: '#FFF',
        }}
      >
        <TextField
          inputRef={inpRef}
          id="ai-search-input"
          variant={'standard'}
          fullWidth
          multiline
          maxRows={fileSidebarAskAI ? 8 : 3}
          placeholder={
            fileSidebarAskAI
              ? T.translate('case.askAnythingAboutTheDocument')
              : isNewSearch
                ? T.translate('case.topicInputPlaceholder')
                : T.translate('case.questionInputPlaceholder')
          }
          sx={{
            borderRadius: '1rem',
            '.MuiOutlinedInput-root': { p: 0 },
            padding: isNewSearch ? '0.1rem' : '0.5rem',
            // Remove container scrolling to prevent double scroll
            '& .MuiInputBase-root': {
              overflow: 'visible',
            },
            // Let the textarea handle scrolling naturally
            '& .MuiInputBase-inputMultiline': {
              resize: 'none',
            },
          }}
          value={filters.phrase}
          onChange={(e) => {
            handleChangeFilters({ phrase: e.target.value });
            if (e.target.value && e.target.value.length > 0 && !openRecentSearches) {
              setRecentSearchesAnchorEl(e.currentTarget);
            } else if (openRecentSearches && e.target.value.length === 0) {
              setRecentSearchesAnchorEl(null);
            }
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
            e.stopPropagation();

            // Allow Shift+Enter for new line
            if (e.key === 'Enter' && e.shiftKey) {
              // Let the default behavior happen (new line)
              return;
            }

            // Handle Enter key for submission
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault(); // Prevent default to avoid newline
              if (filters.phrase) {
                startSearch(filters.phrase);
              }
            }
          }}
          onClick={() => inpRef.current?.focus()}
          autoFocus
          autoComplete="off"
          disabled={fetchDocumentsPending || generateAnswersPending}
          InputProps={{ disableUnderline: true, sx: { fontSize: '1.3rem' } }}
        />
        <div
          style={{
            marginTop: '0.5rem',
            paddingLeft: fileSidebarAskAI && !isNewSearch ? '0.5rem' : '0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <Stack direction="row" spacing={1}>
              <Chip
                clickable
                size="small"
                icon={<MarginOutlined />}
                sx={{
                  backgroundColor: '#F7F7F8',
                  fontSize: '1rem',
                  maxWidth: '20rem',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
                label={`${T.translate('generic.answerFormat')}: ${getOutputFormatLabel(selectedOutputFormat)}`}
                onClick={(e) => setOutputFormatAnchorEl(e.currentTarget)}
              />
              {!fileSidebarAskAI && (
                <>
                  {isNewSearch && (
                    <>
                      <Chip
                        size="small"
                        icon={<FilterListOutlined />}
                        sx={{
                          backgroundColor: '#F7F7F8',
                          fontSize: '1rem',
                          maxWidth: file ? '25rem' : '35rem',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                        label={
                          <SearchFiltersChip
                            filters={filters}
                            isAnswersMode={isAnswersMode}
                            file={file}
                            type="location"
                          />
                        }
                        clickable
                        onClick={(e) => setFiltersMenu(e.currentTarget)}
                      />
                      <Chip
                        size="small"
                        icon={<FilterListOutlined />}
                        sx={{
                          backgroundColor: '#F7F7F8',
                          fontSize: '1rem',
                          maxWidth: file ? '25rem' : '35rem',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                        label={
                          <SearchFiltersChip
                            filters={filters}
                            isAnswersMode={isAnswersMode}
                            file={file}
                            type="files"
                          />
                        }
                        clickable
                        onClick={() => setFilesPopupOpen(true)}
                      />
                    </>
                  )}
                  <Chip
                    size="small"
                    icon={<TuneOutlined />}
                    sx={{
                      backgroundColor: '#F7F7F8',
                      fontSize: '1rem',
                      maxWidth: '10rem',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    label="Options"
                    clickable
                    onClick={(e) => setSearchOptionsAnchorEl(e.currentTarget)}
                  />
                </>
              )}
            </Stack>
          </div>

          <IconButton
            disabled={filters.phrase?.length === 0}
            onClick={() => startSearch(filters.phrase)}
          >
            <SendOutlined />
          </IconButton>
        </div>
        {openSearchOptions && searchOptionsAnchorEl && !fileSidebarAskAI && (
          <SearchOptions
            open={openSearchOptions}
            anchorEl={searchOptionsAnchorEl}
            handleClose={() => {
              setSearchOptionsAnchorEl(null);
              const searchInp = document.getElementById('ai-search-input');
              if (searchInp) {
                searchInp.click();
              }
            }}
          />
        )}
        <TrialBundlesMenu
          value={filters.location}
          onChange={(newValue: any) => handleChangeFilters({ location: newValue })}
          anchorEl={filtersMenu}
          isOpen={openFiltersMenu}
          onClose={() => setFiltersMenu(null)}
        />
        {openOutputFormatMenu && outputFormatAnchorEl && (
          <OutputFormatMenu
            anchorEl={outputFormatAnchorEl}
            open={openOutputFormatMenu}
            onClose={() => setOutputFormatAnchorEl(null)}
            onSelect={handleOutputFormatSelect}
          />
        )}
        {openRecentSearches && recentSearchesAnchorEl && filters?.phrase && (
          <RecentSearchesList
            handleRecentSearchesMenuClose={handleRecentSearchesMenuClose}
            anchorEl={recentSearchesAnchorEl}
            openRecentSearchesMenu={openRecentSearches}
            isAnswersMode={isAnswersMode}
            handleChangeFilters={handleChangeFilters}
            currentInput={filters.phrase}
          />
        )}

        {/* Files Selection Dialog */}
        <FilesSelectionDialog
          open={filesPopupOpen}
          onClose={handleCloseFilesPopup}
          onSave={handleSaveFiles}
          initialSelectedFiles={(filters.files as FileItem[]) || []}
        />
      </div>
      {isNewSearch && !fileSidebarAskAI && (
        <div style={{ marginTop: '1rem', paddingLeft: '10px' }}>
          {(filters.location?.length > 0 || filters.files?.length > 0) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                maxHeight: '300px',
              }}
            >
              {/* Locations Column */}
              {filters.location?.length > 0 && (
                <div
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '0.5rem',
                    padding: '1rem 0.5rem',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography variant="h6" sx={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    {T.translate('case.folders')} ({filters.location.length})
                  </Typography>
                  <div
                    style={{
                      maxHeight: '25rem',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#c1c1c1 #f1f1f1',
                    }}
                    className="locations-scrollbar"
                  >
                    {filters.location.map((location: any) => (
                      <div
                        key={location.id}
                        style={{
                          padding: '0.75rem',
                          border: '1px solid #d0d0d0',
                          borderRadius: '0.25rem',
                          backgroundColor: '#fff',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            wordBreak: 'break-word',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {location.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontFamily: 'monospace',
                          }}
                        >
                          ID: {location.id}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files Column */}
              {filters.files?.length > 0 && (
                <div
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '0.5rem',
                    padding: '1rem 0.5rem',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography variant="h6" sx={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    {T.translate('case.files')} ({filters.files.length})
                  </Typography>
                  <div
                    style={{
                      maxHeight: '25rem',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#c1c1c1 #f1f1f1',
                    }}
                    className="files-scrollbar"
                  >
                    {filters.files.map((fileItem: any) => (
                      <div
                        key={fileItem.id}
                        style={{
                          padding: '0.75rem',
                          border: '1px solid #d0d0d0',
                          borderRadius: '0.25rem',
                          backgroundColor: '#fff',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            wordBreak: 'break-word',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {fileItem.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontFamily: 'monospace',
                          }}
                        >
                          ID: {fileItem.id}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
